import { useEffect, useState } from 'react';

/**
 * Auto-rotating image carousel for product cards. Crossfades between `images`
 * on a timer; falls back to a single static image when there's nothing to rotate.
 * @param {Object} props
 * @param {string[]} props.images - Image URLs to rotate through
 * @param {string} props.alt - Alt text for the images
 * @param {string} [props.className] - Extra classes for the outer wrapper
 * @param {number} [props.intervalMs] - Time between slides
 */
const ImageCarousel = ({ images, alt, className = '', intervalMs = 3000 }) => {
  const slides = (images || []).filter(Boolean);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [slides.length, intervalMs]);

  if (slides.length === 0) {
    return null;
  }

  if (slides.length === 1) {
    return <img src={slides[0]} alt={alt} className={`w-full h-48 object-contain ${className}`} />;
  }

  return (
    <div className={`relative w-full h-48 ${className}`}>
      {slides.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          className={`absolute inset-0 w-full h-48 object-contain transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 pb-1">
        {slides.map((_, i) => (
          <span
            key={i}
            className={`w-1.5 h-1.5 rounded-full ${i === index ? 'bg-gray-700' : 'bg-gray-300'}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
